import { useRef, useState, useEffect, useCallback } from "react";

// Detects scroll position to show/hide overflow masks when content is scrollable
const useMainNavigationScrollDetection = () => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const threshold = 1;

    setCanScrollUp(scrollTop > threshold);
    setCanScrollDown(scrollTop < scrollHeight - clientHeight - threshold);
  }, []);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    // Defer initial check to avoid synchronous setState
    const timeoutId = setTimeout(checkScrollPosition, 0);

    element.addEventListener("scroll", checkScrollPosition, { passive: true });

    // Listen for clicks that might expand/collapse groups
    const handleClick = () => {
      requestAnimationFrame(checkScrollPosition);
      setTimeout(checkScrollPosition, 300); // additional check to check after the animation completes
    };
    element.addEventListener("click", handleClick, { passive: true, capture: true });

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(checkScrollPosition);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-expanded", "data-expanded", "open"],
    });

    return () => {
      clearTimeout(timeoutId);
      element.removeEventListener("scroll", checkScrollPosition);
      element.removeEventListener("click", handleClick);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [checkScrollPosition]);

  return {
    scrollContainerRef,
    canScrollUp,
    canScrollDown,
  };
};

export default useMainNavigationScrollDetection;
